import React from "react";
import { Dispatcher, MarkerStore, BookStore, HighlightStore } from "../../flux";
import { Isvg, Menu, MenuMixin, ToggleButton } from "../../components";
import MarkdownContainer from "../../../reader/notebook/markdown-container";
import YouTube from "../../../../helpers/youtube";
import ProgressArc from "../../../ui_elements/progressArc";

/*
  Renders the Review Mode container
    props:
    state:
*/
class ReviewContainer extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      highlightIndex: 0,
      thanksForRating: false,
      finishedProgress: 0,
      reviewComplete: false,
      didRate: false,
      reviewModeFeedback: "",
      bookView: false
    };

    // MenuMixin bindings
    this._toggleMenu = MenuMixin._toggleMenu.bind(this);
    this._showMenu = MenuMixin._showMenu.bind(this);
    this._removeMenu = MenuMixin._removeMenu.bind(this);
    this._redrawMenu = MenuMixin._redrawMenu.bind(this);

    // Method bindings
    this._highlightsRender = this._highlightsRender.bind(this);
    this._windowBlur = this._windowBlur.bind(this);
    this._nextHighlight = this._nextHighlight.bind(this);
    this._finishedAnimation = this._finishedAnimation.bind(this);
    this._focusCurrentHighlight = this._focusCurrentHighlight.bind(this);
    this._prevHighlight = this._prevHighlight.bind(this);
    this._selectChapter = this._selectChapter.bind(this);
    this._selectHighlighter = this._selectHighlighter.bind(this);
    this._likeReviewMode = this._likeReviewMode.bind(this);
    this._dislikeReviewMode = this._dislikeReviewMode.bind(this);
    this._thanksForRating = this._thanksForRating.bind(this);
    this._rateLater = this._rateLater.bind(this);
    this._reviewAgain = this._reviewAgain.bind(this);
    this._newReview = this._newReview.bind(this);
    this._exitReview = this._exitReview.bind(this);
    this._updateFeedback = this._updateFeedback.bind(this);
    this._submitFeedback = this._submitFeedback.bind(this);
    this._progressMenuButton = this._progressMenuButton.bind(this);
    this._bookView = this._bookView.bind(this);
    this._youTubeReview = this._youTubeReview.bind(this);

    // Refs
    this.userInitialsRef = React.createRef();
    this.thanksForRatingRef = React.createRef();

    this.titles = {};
    this.highlighterNames = {};
  }

  _getInitialState() {
    return {
      highlightIndex: 0,
      thanksForRating: false,
      finishedProgress: 0,
      reviewComplete: false,
      didRate: false,
      reviewModeFeedback: "",
      bookView: false
    };
  }

  componentDidMount() {
    this._focusCurrentHighlight();
    if (!Fraction.User.labs["review-mode"]) {
      Fraction.Helpers.Labs.enableLab("review-mode");
    }
    window.addEventListener("blur", this._windowBlur);
    Dispatcher.setMaxListeners(Dispatcher.getMaxListeners() + 1);
    Dispatcher.bind(Dispatcher.HIGHLIGHTS_RENDER, this._highlightsRender);
  }

  componentWillUnmount() {
    window.removeEventListener("blur", this._windowBlur);
    Dispatcher.unbind(Dispatcher.HIGHLIGHTS_RENDER, this._highlightsRender);
    Dispatcher.setMaxListeners(Dispatcher.getMaxListeners() - 1);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.guid !== this.props.guid) {
      this.setState(this._getInitialState());
    }
  }

  _highlightsRender(isbn) {
    const book = BookStore.get(isbn);
    const highlight = this.props.highlights[this.state.highlightIndex];
    book.viewer.Book.focusHighlight({
      syncId: highlight.syncId,
      skipNavigation: true
    });
  }

  _windowBlur() {
    Fraction.Helpers.focusOnElement(".progress");
  }

  _nextHighlight() {
    var newIndex = this.state.highlightIndex + 1;
    if (newIndex === this.props.highlights.length) {
      // Yay! You're done!
      var userData = Fraction.Helpers.userData();
      userData["completed-review-mode"] =
        userData["completed-review-mode"] || 0;
      Fraction.Helpers.Users.saveUserData(
        "completed-review-mode",
        userData["completed-review-mode"] + 1
      );
      this.setState(
        {
          bookView: false,
          reviewComplete: true,
          highlightIndex: newIndex,
          finishedProgress: 0
        },
        this._finishedAnimation
      );

      Fraction.Reporter.gaEvent("review-mode-completed-review");
    } else {
      this.setState(
        { highlightIndex: newIndex },
        function() {
          this._focusCurrentHighlight();
        }.bind(this)
      );
    }
    this._windowBlur();
  }

  _finishedAnimation() {
    if (this.state.finishedProgress < 100) {
      setTimeout(
        function() {
          this.setState(
            { finishedProgress: this.state.finishedProgress + 2 },
            this._finishedAnimation
          );
        }.bind(this),
        10
      );
    }
  }

  _focusCurrentHighlight() {
    const book = BookStore.get(this.props.isbn);
    const highlight = this.props.highlights[this.state.highlightIndex];
    if (highlight && book.viewer) {
      book.viewer.Book.focusHighlight({
        syncId: highlight.syncId,
        subscription: highlight.subscribed
      });
    }
  }

  _prevHighlight() {
    if (this.state.highlightIndex > 0) {
      this.setState(
        { highlightIndex: Math.max(0, this.state.highlightIndex - 1) },
        () => {
          this._focusCurrentHighlight();
        }
      );
      this._windowBlur();
    }
  }

  _selectChapter(e) {
    var title = $(e.currentTarget).data("key");
    this.titles[title].isChecked = !this.titles[title].isChecked;
    this.highlighters = false;
    this.forceUpdate();
  }

  _selectHighlighter(e) {
    var name = $(e.currentTarget).data("key");
    this.highlighterNames[name].isChecked = !this.highlighterNames[name]
      .isChecked;
    this.forceUpdate();
  }

  _likeReviewMode(e) {
    Jigsaw.Labs.like("review-mode");
    Fraction.User.labs["review-mode"].rating = 1;
    this._thanksForRating();
  }

  _dislikeReviewMode(e) {
    Jigsaw.Labs.dislike("review-mode");
    Fraction.User.labs["review-mode"].rating = -1;
    this._thanksForRating();
  }

  _thanksForRating() {
    this.setState(
      { thanksForRating: true, didRate: true, reviewModeFeedback: "" },
      function() {
        setTimeout(
          function() {
            $(this.thanksForRatingRef.current).fadeOut(
              100,
              function() {
                this.setState({ thanksForRating: false });
              }.bind(this)
            );
          }.bind(this),
          500
        );
      }.bind(this)
    );
  }

  _rateLater(e) {
    this.setState({ didRate: true });
  }

  _reviewAgain(e) {
    this.setState(this._getInitialState(), () => {
      const book = BookStore.get(this.props.isbn);
      const highlight = this.props.highlights[this.state.highlightIndex];
      if (highlight && book.viewer) {
        book.viewer.Book.focusHighlight({
          syncId: highlight.syncId,
          subscription: highlight.subscribed
        });
      }
    });
  }

  _newReview(e) {
    Dispatcher.emit(Dispatcher.CHANGE_CHROME, "");
    Dispatcher.emit("Extension.Launch.review-mode", this.props.isbn);
  }

  _exitReview(e) {
    Dispatcher.emit(Dispatcher.CHANGE_CHROME, "");
  }

  _updateFeedback(e) {
    this.setState({ reviewModeFeedback: $(e.currentTarget).val() });
  }

  _submitFeedback(e) {
    Fraction.Helpers.Labs.submitFeedback(
      this.state.reviewModeFeedback,
      "Review Mode",
      this.props.isbn
    );
    Fraction.Helpers.Users.saveUserData("submitted-review-mode-feedback", true);
    this._thanksForRating();
  }

  _progressMenuButton(e) {
    this._toggleMenu(e);
  }

  _bookView(e) {
    this.setState({ bookView: !this.state.bookView });
  }

  _youTubeReview(note) {
    if (note) {
      return <div id="youTubeFrame">{YouTube._renderYouTubeVideo(note)}</div>;
    }
  }

  render() {
    var progress = Math.ceil(
      (this.state.highlightIndex / this.props.highlights.length) * 100.0
    );
    var showProgress = !this.state.reviewComplete
      ? progress
      : this.state.finishedProgress;
    var progressDOM = (
      <ProgressArc
        reviewComplete={this.state.reviewComplete}
        showProgress={showProgress}
      />
    );

    if (!this.state.reviewComplete) {
      const currentHighlight = this.props.highlights[this.state.highlightIndex];
      const currentMarker = MarkerStore.get(currentHighlight.markerId);
      var progressMenu = (
        <Menu
          key="review-mode-progress-menu"
          menuId="review-mode-progress-menu"
          yOffset={7}
          xOffset={2}
          direction="up"
        >
          <div className="progress-header">
            {i18n.t("base.progress").toUpperCase()}
          </div>
          <div className="progress-content">
            <div>
              {i18n.t("review-mode.completed")}:{" "}
              <span className="progress-number">
                {this.state.highlightIndex}
              </span>
            </div>
            <div>
              {i18n.t("review-mode.to-go")}:{" "}
              <span className="progress-number">
                {this.props.highlights.length - this.state.highlightIndex}
              </span>
            </div>
          </div>
        </Menu>
      );

      var highlightStyle = {};
      var userInitials = "";
      if (currentHighlight.subscribed) {
        highlightStyle = {
          borderColor: currentHighlight.vstColorDark
        };
        userInitials = (
          <div
            className="user-initials"
            title={currentHighlight.authorName}
            ref={this.userInitialsRef}
          >
            {currentHighlight.userInitials}
          </div>
        );
      } else {
        highlightStyle = {
          background: currentHighlight.vstColor
        };
        userInitials = "";
      }
      return (
        <div
          className={"review-mode " + (this.state.bookView ? "book-view" : "")}
        >
          <div className="review-material">
            {progressDOM}
            {progressMenu}
            <h3 className="review-note-highlight-header">
              {i18n.t("review-mode.review-notes-highlights")}
            </h3>
            {userInitials}
            <div className="marker-label">{currentMarker.label}</div>
            <div className="highlight-date">
              {currentHighlight.lastModifiedString}
            </div>
            <div
              className={
                (currentHighlight.subscribed ? "subscribed" : "") +
                " highlight-text"
              }
              style={highlightStyle}
            >
              {currentHighlight.selectedText}
            </div>
            <div className="note-text">
              <MarkdownContainer markdown={currentHighlight.noteText} />
              {this._youTubeReview(currentHighlight.noteText)}
            </div>
            <div className="inline-div" />
          </div>
          <div className="review-footer">
            <button
              aria-disabled={this.state.highlightIndex === 0}
              onClick={this._prevHighlight}
              className={
                (this.state.highlightIndex === 0 ? "disabled" : "") +
                " noButton"
              }
            >
              {i18n.t("base.previous")}
            </button>
            <button className="book-view submit" onClick={this._bookView}>
              {this.state.bookView
                ? i18n.t("review-mode.details-view")
                : i18n.t("review-mode.book-view")}
            </button>
            <button onClick={this._nextHighlight} className="noButton">
              {i18n.t("base.next")}
            </button>
          </div>
        </div>
      );
    } else {
      var finishedReview = <div>{i18n.t("review-mode.finished-review")}</div>;
      var userData = Fraction.Helpers.userData();
      if (
        !this.state.didRate &&
        userData["completed-review-mode"] > 0 &&
        userData["completed-review-mode"] % 5 === 0 &&
        !userData["submitted-review-mode-feedback"]
      ) {
        // you have reviewed 5 times, and we don't know that you've left feedback.
        finishedReview = (
          <div>
            <div className="success-image">{progressDOM}</div>
            <div className="main-text">
              {i18n.t("review-mode.finished-review")}
            </div>
            <div className="sub-text">{i18n.t("labs.want-your-feedback")}</div>
            <div className="feedback">
              <textarea
                rows="1"
                onChange={this._updateFeedback}
                aria-label={i18n.t("labs.what-did-you-think", {
                  lab: i18n.t("review-mode.review-mode")
                })}
                placeholder={i18n.t("labs.what-did-you-think", {
                  lab: i18n.t("review-mode.review-mode")
                })}
                value={this.state.reviewModeFeedback || ""}
              />
              <button
                className="submit large-button"
                onClick={this._submitFeedback}
              >
                {i18n.t("base.submit")}
              </button>
            </div>
            <button className="noButton not-now" onClick={this._rateLater}>
              {i18n.t("labs.not-now")}
            </button>
          </div>
        );
      } else if (
        !this.state.didRate &&
        Fraction.User.labs["review-mode"].rating === 0
      ) {
        // yeah, let's review review mode!
        finishedReview = (
          <div>
            <div className="success-image">{progressDOM}</div>
            <div className="main-text">
              {i18n.t("review-mode.finished-review")}
            </div>
            <div className="sub-text">
              {i18n.t("labs.what-did-you-think", {
                lab: i18n.t("review-mode.review-mode")
              })}
            </div>
            <div className="rate">
              <button className="noButton" onClick={this._likeReviewMode}>
                <Isvg src="/img/thumbs_up_gray.svg">NS</Isvg>
              </button>
              <button className="noButton" onClick={this._dislikeReviewMode}>
                <Isvg src="/img/thumbs_down_gray.svg">NS</Isvg>
              </button>
            </div>
            <button className="noButton not-now" onClick={this._rateLater}>
              {i18n.t("labs.not-now")}
            </button>
          </div>
        );
      } else {
        finishedReview = (
          <div className={this.state.didRate ? "rated" : ""}>
            {!this.state.didRate && (
              <div className="success-image">{progressDOM}</div>
            )}
            <div className="main-text">
              {!this.state.didRate
                ? i18n.t("review-mode.finished-review")
                : i18n.t("review-mode.do-next")}
            </div>
            <div className="sub-text">
              {!this.state.didRate ? i18n.t("review-mode.do-next") : ""}
            </div>
            <div className="after-review-options">
              <button
                className="submit large-button"
                onClick={this._reviewAgain}
              >
                {i18n.t("review-mode.review-again")}
              </button>
              <button
                className="submit lightgray large-button"
                onClick={this._newReview}
              >
                {i18n.t("review-mode.new-review")}
              </button>
              <button
                className="submit lightgray large-button"
                onClick={this._exitReview}
              >
                {i18n.t("review-mode.exit-review-mode")}
              </button>
            </div>

            {this.state.thanksForRating && (
              <div className="thanks-for-rating" ref={this.thanksForRatingRef}>
                <img src="/img/checkmark_success.svg" />
                <div className="main-text">
                  {i18n.t("review-mode.finished-review")}
                </div>
              </div>
            )}
          </div>
        );
      }
      return (
        <div className="review-mode">
          <div className="review-finished">{finishedReview}</div>
        </div>
      );
    }
  }
}

module.exports = ReviewContainer;
